import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import TopBar, { HERO_IMG } from "@/components/TopBar";
import { PromoBanner } from "@/components/PromoBanner";
import CartDrawer from "@/components/CartDrawer";
import MenuItemCard from "@/components/MenuItemCard";
import CategoryHeader from "@/components/CategoryHeader";
import HomeLoyaltyCard from "@/components/HomeLoyaltyCard";
import ActiveOrderStrip from "@/components/ActiveOrderStrip";
import ReviewsSummaryStrip from "@/components/ReviewsSummaryStrip";
import StatusBanner from "@/components/StatusBanner";
import FeaturedCarousel from "@/components/FeaturedCarousel";
import { InstallAppBanner } from "@/hooks/usePWAManifest";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { T } from "@/context/TextEditContext";
import { Phone, Clock, ShoppingBag, Download, X, MapPin, Navigation, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import SoundtrackPlayer from "@/components/SoundtrackPlayer";

export default function HomePage() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [installEvent, setInstallEvent] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(
    () => localStorage.getItem("bk_install_dismissed") === "1"
  );
  const { count, subtotal } = useCart();
  const { settings } = useSiteSettings();

  // Categories come from site settings (admin-editable). Add-Ons is
  // structurally required but never customer-visible so we drop it here.
  const CATEGORIES = useMemo(
    () =>
      (settings.menu_categories || []).filter((c) => c && c !== "Add-Ons"),
    [settings.menu_categories]
  );
  const [activeCat, setActiveCat] = useState(CATEGORIES[0] || "");
  const [topH, setTopH] = useState(104);

  // Keep the sticky category strip glued right under the (variable-height) top bar.
  useEffect(() => {
    const el = document.querySelector('[data-testid="top-bar"]');
    if (!el) return;
    const ro = new ResizeObserver(() => setTopH(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep activeCat valid when the admin renames/removes the current one.
  useEffect(() => {
    if (CATEGORIES.length && !CATEGORIES.includes(activeCat)) {
      setActiveCat(CATEGORIES[0]);
    }
  }, [CATEGORIES, activeCat]);

  useEffect(() => {
    const pull = () => api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
    pull();
    setLoading(false);
    // Refresh so newly added items / sold-out changes appear without reload.
    const t = setInterval(pull, 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const promptInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };

  const dismissInstall = () => {
    localStorage.setItem("bk_install_dismissed", "1");
    setInstallDismissed(true);
  };

  const grouped = useMemo(() => {
    const g = {};
    CATEGORIES.forEach((c) => (g[c] = []));
    const q = search.trim().toLowerCase();
    menu.forEach((m) => {
      if (
        q &&
        !m.name.toLowerCase().includes(q) &&
        !m.description.toLowerCase().includes(q)
      )
        return;
      if (g[m.category]) g[m.category].push(m);
    });
    return g;
  }, [menu, search, CATEGORIES]);

  const scrollToCat = (cat) => {
    setActiveCat(cat);
    const el = document.getElementById(`cat-${cat}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Scroll-spy: highlight the category whose section is under the sticky strip.
  useEffect(() => {
    if (loading || !CATEGORIES.length) return;
    const onScroll = () => {
      const line = topH + 70;
      let current = CATEGORIES[0];
      for (const c of CATEGORIES) {
        const el = document.getElementById(`cat-${c}`);
        if (el && el.getBoundingClientRect().top <= line) current = c;
      }
      setActiveCat((prev) => (prev === current ? prev : current));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [CATEGORIES, loading, topH]);

  // Keep the active chip visible inside the horizontally scrolling strip.
  useEffect(() => {
    const btn = document.querySelector(`[data-testid="category-filter-btn-${activeCat}"]`);
    const strip = btn?.parentElement;
    if (!btn || !strip) return;
    strip.scrollTo({ left: btn.offsetLeft - strip.clientWidth / 2 + btn.clientWidth / 2, behavior: "smooth" });
  }, [activeCat]);

  return (
    <div className="min-h-[100dvh] bg-stone-100 flex justify-center overflow-x-clip">
      <InstallAppBanner
        manifest="/manifest.json"
        themeColor="#C0392B"
        appleTitle="Blossom's Kitchen"
        label="Blossom's Kitchen"
      />
      <div className="w-full max-w-md lg:max-w-5xl 2xl:max-w-7xl min-h-[100dvh] bg-[#FDFBF7] rustic-paper phone-shell relative overflow-x-clip">
        <TopBar
          onOpenCart={() => setCartOpen(true)}
          showSearch
          onSearch={setSearch}
          searchValue={search}
        />

        {/* Hero */}
        <div className="relative h-52 sm:h-64 lg:h-80 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMG})` }}
          />
          <div className="absolute inset-0 bg-stone-900/55" />
          <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
            <div className="font-hand text-brand-yellow text-2xl mb-1">
              <T field="hero_welcome" />
            </div>
            <h1 className="font-display text-white text-4xl lg:text-6xl leading-tight drop-shadow-lg">
              <T field="brand_name" />
            </h1>
            <p className="mt-2 text-white/90 text-xs lg:text-sm uppercase tracking-widest font-bold max-w-[16rem] lg:max-w-md">
              <T field="hero_tagline" />
            </p>
            <button
              data-testid="hero-order-now-btn"
              onClick={() => scrollToCat(CATEGORIES[0] || "Burgers")}
              className="mt-3 sm:mt-4 bg-brand-yellow text-stone-900 px-7 py-3 rounded-full font-black uppercase tracking-widest text-xs sm:text-sm hover:bg-yellow-400 transition-all active:scale-95 shadow-lg"
            >
              <ShoppingBag className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              <T field="hero_cta_label" />
            </button>
          </div>
        </div>

        {/* Owner attribution — proudly local */}
        <div
          data-testid="owner-attribution"
          className="chalkboard-band px-4 py-3 text-center border-b-4 border-brand-red"
        >
          <div className="text-xs uppercase tracking-widest text-white font-black">
            <T field="owner_line" />
          </div>
          <div className="font-hand text-brand-yellow text-3xl leading-tight mt-0.5">
            <T field="owner_names" />
          </div>
        </div>

        {/* Active order — auto-shows when customer has an in-progress order */}
        <ActiveOrderStrip />
        <PromoBanner />

        {/* Jobs pill — tap to open the application */}
        {/* Jobs pill — hidden when the owner pauses hiring */}
        {settings.hiring_open !== false && (
        <div className="flex justify-center mt-3 px-4">
          <Link
            to="/jobs"
            data-testid="jobs-pill"
            className="inline-flex items-center gap-2 bg-stone-900 text-white pl-4 pr-5 py-2.5 rounded-full shadow-md border border-stone-700 hover:brightness-110 transition-all active:scale-[0.98]"
          >
            <span className="w-6 h-6 rounded-full bg-brand-yellow flex items-center justify-center">
              <Briefcase className="w-3.5 h-3.5 text-stone-900" />
            </span>
            <span className="text-xs font-black uppercase tracking-widest">
              <T k="home.jobs_pill" d="We're Hiring — Apply Here" />
            </span>
          </Link>
        </div>
        )}

        {/* Optional cute seasonal decoration banner (uploaded per theme) */}
        {settings.active_theme &&
          settings.active_theme !== "default" &&
          settings.theme_images?.[settings.active_theme] && (
            <img
              src={settings.theme_images[settings.active_theme]}
              alt="Seasonal decoration"
              data-testid="theme-banner-img"
              className="mx-4 mt-3 rounded-xl w-[calc(100%-2rem)] object-cover max-h-40 lg:max-h-56 border border-stone-200 shadow-sm"
            />
          )}

        {/* Status banner + featured carousel — side-by-side on desktop when both exist */}
        <div
          className={
            (settings.featured_items || []).some((it) => it && it.image_url)
              ? "lg:grid lg:grid-cols-2 lg:items-start"
              : ""
          }
        >
          <StatusBanner />
          <FeaturedCarousel />
        </div>

        {/* Reviews summary + loyalty card — paired columns on desktop */}
        <div className="lg:grid lg:grid-cols-2 lg:items-stretch">
          <ReviewsSummaryStrip />
          <HomeLoyaltyCard />
        </div>
        <SoundtrackPlayer />

        {/* Install app prompt (Android/Chrome) */}
        {installEvent && !installDismissed && (
          <div
            data-testid="install-prompt"
            className="mx-4 mt-3 bg-brand-yellow/20 border-2 border-brand-yellow rounded-xl p-3 flex items-center gap-3 animate-float-in"
          >
            <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-brand-yellow" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-stone-900 text-sm leading-tight">
                <T k="home.install_title" d="Install Blossom's" />
              </div>
              <div className="text-[11px] text-stone-600 leading-tight">
                <T k="home.install_sub" d="Add to home screen for one-tap ordering" />
              </div>
            </div>
            <button
              onClick={promptInstall}
              data-testid="install-prompt-accept"
              className="bg-brand-red text-white px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold hover:bg-red-800 whitespace-nowrap"
            >
              Install
            </button>
            <button
              onClick={dismissInstall}
              data-testid="install-prompt-dismiss"
              className="text-stone-500 hover:text-stone-800 p-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Info strip */}
        <div className="bg-brand-red text-white text-xs px-4 py-1 flex items-center justify-around">
          <a
            href={`tel:+1${settings.phone.replace(/\D/g, "")}`}
            className="flex items-center gap-1.5 min-h-[40px] px-2"
            data-testid="truck-phone-link"
          >
            <Phone className="w-3 h-3" /> <T field="phone" />
          </a>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> <T field="hours" />
          </span>
        </div>

        {/* Truck locator card — visible when owner toggles it on in Admin */}
        {settings.truck_location_active && settings.truck_location_name && (
          <a
            href={
              settings.truck_location_address
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${settings.truck_location_name} ${settings.truck_location_address}`
                  )}`
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            data-testid="truck-locator-card"
            className="mx-4 mt-3 flex items-start gap-3 p-3 rounded-xl bg-brand-yellow/95 border-2 border-brand-red shadow-sm hover:brightness-105 active:brightness-95 transition"
          >
            <div className="w-9 h-9 rounded-full bg-brand-red flex items-center justify-center shrink-0">
              <MapPin className="w-4 h-4 text-brand-yellow" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-black text-brand-red">
                <T k="home.parked_label" d="Where We're Parked Today" />
              </div>
              <div
                className="font-display text-lg text-stone-900 leading-tight truncate"
                data-testid="truck-locator-name"
              >
                {settings.truck_location_name}
              </div>
              {settings.truck_location_address && (
                <div
                  className="text-xs text-stone-800 mt-0.5"
                  data-testid="truck-locator-address"
                >
                  {settings.truck_location_address}
                </div>
              )}
              {settings.truck_location_note && (
                <div
                  className="text-[11px] text-stone-700 italic mt-0.5"
                  data-testid="truck-locator-note"
                >
                  {settings.truck_location_note}
                </div>
              )}
            </div>
            {settings.truck_location_address && (
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-brand-red shrink-0 self-center">
                <Navigation className="w-3 h-3" />
                <span className="hidden sm:inline">Directions</span>
              </div>
            )}
          </a>
        )}

        {/* Category strip */}
        <div className="sticky z-20 bg-[#FDFBF7] border-b border-stone-200" style={{ top: topH }} data-testid="category-strip">
          <div className="flex gap-2 px-4 py-2.5 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => scrollToCat(c)}
                data-testid={`category-filter-btn-${c}`}
                className={`shrink-0 min-h-[40px] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border-2 transition-all ${
                  activeCat === c
                    ? "bg-brand-red text-white border-brand-red"
                    : "bg-white text-stone-700 border-stone-300 hover:border-brand-red"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Menu list */}
        <div className="px-4 py-4 pb-40 space-y-6" data-testid="menu-list">
          {loading ? (
            <div className="text-center text-stone-400 py-16 font-hand text-3xl">
              <T k="home.loading" d="Warming up the grill..." />
            </div>
          ) : (
            CATEGORIES.map((cat) => (
              <section key={cat} id={`cat-${cat}`} className="space-y-3" style={{ scrollMarginTop: topH + 66 }}>
                <CategoryHeader label={cat} />
                {cat === "Drinks" && settings.drinks_are_free_now && (
                  <div
                    data-testid="drinks-free-banner"
                    className="chalkboard-band px-4 py-3 rounded-lg text-center border-2 border-brand-yellow"
                  >
                    <div className="font-hand text-brand-yellow text-2xl leading-tight">
                      <T field="drinks_free_headline" />
                    </div>
                    {settings.drinks_free_subtitle && (
                      <div className="text-[11px] uppercase tracking-widest text-white/85 font-bold mt-0.5">
                        <T field="drinks_free_subtitle" />
                      </div>
                    )}
                  </div>
                )}
                {grouped[cat].length === 0 ? (
                  <div className="text-center text-stone-400 text-sm py-4">
                    <T k="home.no_match" d="No items match your search" />
                  </div>
                ) : (
                  <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 2xl:grid-cols-3 lg:gap-4 lg:items-start">
                    {grouped[cat].map((item) => (
                      <MenuItemCard key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </section>
            ))
          )}

          <div className="mt-8 bg-white border border-stone-200 rounded-xl p-4 text-center shadow-sm">
            <div className="font-hand text-2xl text-brand-red mb-1">
              <T k="home.advisory_label" d="Advisory" />
            </div>
            <div className="text-sm text-stone-800 leading-relaxed font-medium" data-testid="advisory-text">
              <T field="advisory" />
            </div>
          </div>
        </div>

        <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
        {/* Sticky cart bar */}
        {count > 0 && (
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-20 px-3 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <button
              onClick={() => setCartOpen(true)}
              data-testid="sticky-cart-cta"
              className="w-full bg-brand-red text-white rounded-full py-3.5 px-5 flex items-center justify-between shadow-2xl hover:bg-red-800 transition-all active:scale-[0.98]"
            >
              <span className="font-bold text-sm flex items-center gap-2">
                <span className="bg-brand-yellow text-stone-900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-black">
                  {count}
                </span>
                <T k="home.view_cart" d="View Cart" />
              </span>
              <span className="font-display text-lg">${subtotal.toFixed(2)}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
